const DEFAULT_FLAGS = {
  solar_solutions: false,
  requires_vibration_energy_state: false,
  drycontact_as_water: false,
};

const SHOW_ONLY_CONFIGURED_SENSORS_DEFAULT = true;

function shouldShowOnlyConfiguredSensors() {
  const raw = process.env.VUE_APP_SHOW_ONLY_CONFIGURED_SENSORS;
  if (raw === undefined || raw === null || raw === "") {
    return SHOW_ONLY_CONFIGURED_SENSORS_DEFAULT;
  }

  const normalized = String(raw).trim().toLowerCase();
  if (["0", "false", "no", "off"].includes(normalized)) return false;
  if (["1", "true", "yes", "on"].includes(normalized)) return true;

  return SHOW_ONLY_CONFIGURED_SENSORS_DEFAULT;
}

export function isStrictConfiguredSensorsMode() {
  return shouldShowOnlyConfiguredSensors();
}

const UNKNOWN_SENSOR_TYPE = {
  id: 0,
  key: "unknown",
  label: "Unknown sensor type",
  sensors: [],
  features: [],
  flags: { ...DEFAULT_FLAGS },
};

// Frontend fallback mirror of App\Support\DeviceSensorTypes.
// Runtime data from device.sensor_type_info is the source of truth; this map is
// only for cached/legacy responses that do not include sensor_type_info yet.
export const SENSOR_TYPE_FALLBACKS = {
  0: UNKNOWN_SENSOR_TYPE,
  1: {
    id: 1,
    key: "temperature",
    label: "Temperature",
    sensors: ["temperatures"],
  },
  2: {
    id: 2,
    key: "temperature_humidity",
    label: "Temperature + Humidity",
    sensors: ["temperatures", "humidity"],
  },
  3: {
    id: 3,
    key: "temperature_humidity",
    label: "Temperature + Humidity",
    sensors: ["temperatures", "humidity"],
  },
  4: {
    id: 4,
    key: "temperature_humidity_iaq",
    label: "Temperature + Humidity + IAQ",
    sensors: ["temperatures", "humidity", "iaq"],
  },
  5: {
    id: 5,
    key: "temperature_humidity_pressure",
    label: "Temperature + Humidity + Pressure",
    sensors: ["temperatures", "humidity", "pressure"],
  },
  6: {
    id: 6,
    key: "temperature_humidity_light",
    label: "Temperature + Humidity + Light",
    sensors: ["temperatures", "humidity", "light"],
  },
  7: { id: 7, key: "light", label: "Light", sensors: ["light"] },
  8: { id: 8, key: "motion", label: "Motion", sensors: ["motion"] },
  9: { id: 9, key: "smoke", label: "Smoke", sensors: ["smoke"] },
  10: {
    id: 10,
    key: "water",
    label: "Water leak",
    sensors: ["water"],
    flags: { drycontact_as_water: true },
  },
  11: { id: 11, key: "hall_magnet", label: "Magnet", sensors: ["hall"] },
  12: { id: 12, key: "hall_door", label: "Door", sensors: ["hall"] },
  13: { id: 13, key: "hall_window", label: "Window", sensors: ["hall"] },
  14: {
    id: 14,
    key: "dry_contact",
    label: "Dry contact",
    sensors: ["drycontact"],
  },
  15: { id: 15, key: "pressure", label: "Pressure", sensors: ["pressure"] },
  16: {
    id: 16,
    key: "pulse_counter",
    label: "Pulse counter",
    sensors: ["pulse_counter"],
  },
  17: {
    id: 17,
    key: "electric_counter",
    label: "Electric counter",
    sensors: ["electric_counter"],
  },
  18: {
    id: 18,
    key: "temperature_humidity_co2_pressure",
    label: "Temperature + Humidity + CO2 + Pressure",
    sensors: ["temperatures", "humidity", "co2", "pressure"],
  },
  20: { id: 20, key: "range", label: "Range", sensors: ["range"] },
  21: {
    id: 21,
    key: "vibration",
    label: "Vibration",
    sensors: ["isVibrating"],
    features: [],
    flags: { ...DEFAULT_FLAGS },
  },
  70: {
    id: 70,
    key: "vibration_energy",
    label: "Vibration energy",
    sensors: ["motion", "vibration_energy", "click"],
    flags: { requires_vibration_energy_state: true },
  },
  80: {
    id: 80,
    key: "temperature_humidity_motion",
    label: "Temperature + Humidity + Motion",
    sensors: ["temperatures", "humidity", "motion"],
  },
  // Presses are events/counters, not a persistent binary state. The runtime
  // sensor_type_info response remains authoritative for the exact counter key.
  90: {
    id: 90,
    key: "emergency_button",
    label: "Emergency button",
    sensors: [
      "button",
      "buttonCount",
      "singlePress",
      "doublePress",
      "longPress",
      "singlePressCount",
      "doublePressCount",
      "longPressCount",
    ],
  },
  640: {
    id: 640,
    key: "solar_solutions",
    label: "SolarSolutions",
    sensors: ["alarm_state", "battery_voltage", "load_state", "power_state"],
    features: ["solar_actions"],
    flags: { solar_solutions: true },
  },
};

function normalizeInfo(info, fallbackId = 0) {
  const source = info && typeof info === "object" ? info : {};
  const id = Number(source.id ?? fallbackId ?? 0);

  return {
    id: Number.isNaN(id) ? 0 : id,
    key: source.key || "unknown",
    label: source.label || "Unknown sensor type",
    sensors: Array.isArray(source.sensors) ? [...source.sensors] : [],
    features: Array.isArray(source.features) ? [...source.features] : [],
    flags: {
      ...DEFAULT_FLAGS,
      ...(source.flags && typeof source.flags === "object" ? source.flags : {}),
    },
  };
}

export function getSensorTypeId(device) {
  const fromInfo =
    device && device.sensor_type_info && device.sensor_type_info.id;
  const fromSettings = device && device.settings && device.settings.sensor_type;
  const fromData = device && device.data && device.data.sensor_type;
  const id = Number(fromInfo ?? fromSettings ?? fromData ?? 0);
  return Number.isNaN(id) ? 0 : id;
}

export function getSensorTypeInfo(device) {
  if (device && device.sensor_type_info) {
    return normalizeInfo(device.sensor_type_info, getSensorTypeId(device));
  }

  const id = getSensorTypeId(device);
  return normalizeInfo(SENSOR_TYPE_FALLBACKS[id] || UNKNOWN_SENSOR_TYPE, id);
}

export function getSensorTypeDeclaredSensors(device) {
  return getSensorTypeInfo(device).sensors;
}

export function getLiveSensors(device) {
  return (device && device.data && device.data.sensors) || {};
}

export function getLiveSensorKeys(device) {
  const sensors = getLiveSensors(device);
  return sensors && typeof sensors === "object" && !Array.isArray(sensors)
    ? Object.keys(sensors)
    : [];
}

export function hasLiveSensor(device, sensor) {
  return getLiveSensorKeys(device).includes(sensor);
}

export function getVisibleSensorKeys(device) {
  const declared = getSensorTypeDeclaredSensors(device);
  const live = getLiveSensorKeys(device);

  if (getSensorTypeId(device) === 90) {
    return ["button", "singlePress", "doublePress", "longPress"].filter(
      (sensor) => live.includes(sensor)
    );
  }

  if (!shouldShowOnlyConfiguredSensors()) {
    return live;
  }

  // Strict-by-default: show only configured sensors from sensor_type_info.
  // To avoid empty cards, keep only declared sensors that exist in live payload.
  if (!declared.length) return live;
  return declared.filter((sensor) => live.includes(sensor));
}

export function isVisibleSensor(device, sensor) {
  return getVisibleSensorKeys(device).includes(sensor);
}

export function hasDeclaredSensor(device, sensor) {
  return getSensorTypeDeclaredSensors(device).includes(sensor);
}

export function hasSensorTypeFlag(device, flag) {
  return !!getSensorTypeInfo(device).flags[flag];
}

export function hasSensorTypeFeature(device, feature) {
  return getSensorTypeInfo(device).features.includes(feature);
}

export function isSolarSolutionsDevice(device) {
  return (
    hasSensorTypeFlag(device, "solar_solutions") ||
    hasSensorTypeFeature(device, "solar_actions")
  );
}

export function requiresVibrationEnergyState(device) {
  return hasSensorTypeFlag(device, "requires_vibration_energy_state");
}

export function shouldExposeDryContactAsWater(device) {
  return hasSensorTypeFlag(device, "drycontact_as_water");
}

export function normalizeSensorKey(sensor) {
  return String(sensor || "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .toLowerCase();
}

export function isBinarySensor(sensor) {
  return [
    "water",
    "hall",
    "drycontact",
    "isVibrating",
    "detection",
    "motion",
    "load_state",
    "alarm_state",
    "power_state",
    "light",
    "smoke",
    "click",
  ].includes(sensor);
}

export function isEmergencyButtonDevice(device) {
  return getSensorTypeId(device) === 90;
}

export function hasAvailableBattery(device) {
  const battery = device && device.data && device.data.battery;
  if (!battery || typeof battery !== "object") return false;

  return battery.value !== null && battery.value !== undefined;
}
